/**
 * File-owned annotations: comment threads and markup planes stored as keys on
 * the file's own Automerge document, so they branch, merge and checkpoint with
 * the file. Text, sketch and model files carry them beside their content; a
 * PDF or image carries them on its blob doc (`fs/blob-doc.ts`).
 *
 * This module is what every file type shares. Where a plane sits (a PDF page,
 * a pose in model space) is its frame, which the file's type defines and
 * reads, along with how to show a thread on it: the file's MIME already says
 * which kind of frame its planes have, so the frame doesn't repeat it.
 */
import * as Automerge from "@automerge/automerge";
import type { DocHandle } from "@automerge/automerge-repo";
import { diffChars } from "diff";
import { invariant } from "ts-invariant";

import type { TextDoc } from "../fs/types";

/** A rectangle in its plane's own units. A pin is a zero-size rect. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** A stored tldraw record (shape or binding). Playground doesn't type tldraw. */
export interface PlaneRecord {
  id: string;
  typeName: string;
  [field: string]: unknown;
}

/**
 * A markup surface. Many marks and threads may share one plane. Its frame is
 * the file type's to define and check; here it is opaque.
 */
export interface Plane<Frame = unknown> {
  id: string;
  frame: Frame;
  /** tldraw shapes and bindings, in plane-local coordinates. */
  records: Record<string, PlaneRecord>;
  /** The tldraw serialized schema the records were written with. */
  schema: Record<string, unknown>;
}

/** Text anchors predate `kind`, so theirs is optional. */
export interface TextAnchor {
  kind?: "text";
  /** Automerge cursors into `text`. */
  start: string;
  end: string;
  quote: string;
}

export type SpatialAnchor = (
  | { kind: "region"; plane: string; rect: Rect }
  | { kind: "marks"; plane: string; shapes: string[] }
) & {
  /** Stable names of what the mark is about (model node FQNs, a sheet), not
   *  everything that happens to lie behind it. */
  subjects?: string[];
};

/** Where a thread points, and the revision it was made against. */
export type Anchor = (TextAnchor | SpatialAnchor) & { sourceHeads: string[] };

export interface Message {
  id: string;
  /** A user id, or `agent:<name>` for an agent. */
  author: string;
  /** The author's display name when posted. Absent on the earliest text comments. */
  authorName?: string;
  body: string;
  /** Who the message addresses, e.g. `agent:bear`. Each is notified on its own channel. */
  mentions?: string[];
  createdAt: number;
}

export interface Annotation {
  id: string;
  anchor: Anchor;
  /** Empty for a bare mark: a cloud with nothing to say yet is valid. */
  messages: Record<string, Message>;
  resolved: boolean;
}

/** How chat, `show` and other surfaces point back at one thread. */
export interface AnnotationRef {
  path: string;
  threadId: string;
}

export type AnnotatedDoc = {
  [key: `comment:${string}`]: Annotation;
  [key: `plane:${string}`]: Plane;
};

export interface NewMessage {
  author: string;
  authorName: string;
  body: string;
  mentions: string[];
}

const isCommentKey = (key: string): key is `comment:${string}` =>
  key.startsWith("comment:");
const isPlaneKey = (key: string): key is `plane:${string}` =>
  key.startsWith("plane:");

const firstPosted = (annotation: Annotation) =>
  Math.min(...Object.values(annotation.messages).map((m) => m.createdAt));

/** Every thread on the doc, oldest first; bare marks last. */
export function annotationsOf(doc: AnnotatedDoc): Annotation[] {
  return Object.keys(doc)
    .filter(isCommentKey)
    .flatMap((key) => doc[key] ?? [])
    .sort((a, b) => firstPosted(a) - firstPosted(b));
}

export function planesOf(doc: AnnotatedDoc): Plane[] {
  return Object.keys(doc)
    .filter(isPlaneKey)
    .flatMap((key) => doc[key] ?? []);
}

const isAnnotationKey = (
  key: string,
): key is `comment:${string}` | `plane:${string}` =>
  isCommentKey(key) || isPlaneKey(key);

/** How many threads and planes differ between two versions of a doc. */
export function changedAnnotationCount(
  before: AnnotatedDoc,
  after: AnnotatedDoc,
): number {
  const keys = new Set(
    [...Object.keys(before), ...Object.keys(after)].filter(isAnnotationKey),
  );
  return [...keys].filter(
    (key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]),
  ).length;
}

/**
 * Carry a range from one spelling of a text to another (a Markdown editor's
 * re-serialization of the stored file). Null when the range's own characters
 * differ between the two, so a selection never lands on other text.
 */
export function carryRange(
  from: string,
  to: string,
  range: { start: number; end: number },
): { start: number; end: number } | null {
  let a = 0;
  let b = 0;
  let start = -1;
  let end = -1;
  for (const part of diffChars(from, to)) {
    const length = part.value.length;
    if (!part.added && !part.removed) {
      if (start < 0 && range.start >= a && range.start <= a + length)
        start = b + range.start - a;
      if (end < 0 && range.end >= a && range.end <= a + length)
        end = b + range.end - a;
    }
    if (!part.added) a += length;
    if (!part.removed) b += length;
  }
  return start >= 0 &&
    end >= start &&
    to.slice(start, end) === from.slice(range.start, range.end)
    ? { start, end }
    : null;
}

/** The anchor's current range, or null once its quoted text is gone. */
export function textRange(
  doc: TextDoc,
  anchor: TextAnchor,
): { start: number; end: number } | null {
  const start = Automerge.getCursorPosition(doc, ["text"], anchor.start);
  const end = Automerge.getCursorPosition(doc, ["text"], anchor.end);
  return start >= 0 &&
    end >= start &&
    doc.text?.slice(start, end) === anchor.quote
    ? { start, end }
    : null;
}

export interface PositionedTextAnnotation {
  annotation: Annotation;
  anchor: TextAnchor;
  range: { start: number; end: number } | null;
}

/** The doc's text threads, each with its current range. */
export function textAnnotations(doc: TextDoc): PositionedTextAnnotation[] {
  return annotationsOf(doc).flatMap((annotation) => {
    const { anchor } = annotation;
    return anchor.kind === "region" || anchor.kind === "marks"
      ? []
      : [{ annotation, anchor, range: textRange(doc, anchor) }];
  });
}

function revision<D extends AnnotatedDoc>(handle: DocHandle<D>): string[] {
  const heads = handle.heads();
  invariant(heads?.length, "Annotated document has no revision");
  return [...heads];
}

function posted(message: NewMessage): Message {
  return {
    id: crypto.randomUUID(),
    ...message,
    createdAt: Date.now(),
  };
}

function messagesOf(message: NewMessage | undefined): Record<string, Message> {
  if (!message) return {};
  const first = posted(message);
  return { [first.id]: first };
}

export function addTextAnnotation(
  handle: DocHandle<TextDoc>,
  range: { start: number; end: number; quote: string },
  message: NewMessage,
): string {
  const sourceHeads = revision(handle);
  const id = crypto.randomUUID();
  handle.change((doc) => {
    invariant(
      doc.text?.slice(range.start, range.end) === range.quote,
      "Text selection changed",
    );
    doc[`comment:${id}`] = {
      id,
      anchor: {
        kind: "text",
        start: Automerge.getCursor(doc, ["text"], range.start),
        end: Automerge.getCursor(doc, ["text"], range.end),
        quote: range.quote,
        sourceHeads,
      },
      messages: messagesOf(message),
      resolved: false,
    };
  });
  return id;
}

export function addAnnotation<D extends AnnotatedDoc>(
  handle: DocHandle<D>,
  anchor: SpatialAnchor,
  message?: NewMessage,
): string {
  const sourceHeads = revision(handle);
  const id = crypto.randomUUID();
  handle.change((doc: AnnotatedDoc) => {
    invariant(doc[`plane:${anchor.plane}`], `Unknown plane: ${anchor.plane}`);
    doc[`comment:${id}`] = {
      id,
      anchor: { ...anchor, sourceHeads },
      messages: messagesOf(message),
      resolved: false,
    };
  });
  return id;
}

export function replyToAnnotation<D extends AnnotatedDoc>(
  handle: DocHandle<D>,
  id: string,
  message: NewMessage,
): void {
  handle.change((doc: AnnotatedDoc) => {
    const thread = doc[`comment:${id}`];
    invariant(thread, `Unknown comment thread: ${id}`);
    const reply = posted(message);
    thread.messages[reply.id] = reply;
  });
}

export function setAnnotationResolved<D extends AnnotatedDoc>(
  handle: DocHandle<D>,
  id: string,
  resolved: boolean,
): void {
  handle.change((doc: AnnotatedDoc) => {
    const thread = doc[`comment:${id}`];
    invariant(thread, `Unknown comment thread: ${id}`);
    thread.resolved = resolved;
  });
}

export function addPlane<D extends AnnotatedDoc, Frame extends object>(
  handle: DocHandle<D>,
  frame: Frame,
  schema: Record<string, unknown>,
): string {
  const id = crypto.randomUUID();
  handle.change((doc: AnnotatedDoc) => {
    doc[`plane:${id}`] = { id, frame, records: {}, schema };
  });
  return id;
}

/**
 * A mark's thread. Every shape drawn on a plane has one, its id taken from
 * the shape's, so two people drawing at once make one thread, not two.
 */
export const markThreadId = (shapeId: string) =>
  `mark-${shapeId.replace(/^shape:/, "")}`;

/** Top-level shapes: what a person drew, not a part of something drawn. */
function drawnShapes(plane: Plane): PlaneRecord[] {
  return Object.values(plane.records).filter(
    (record) =>
      record.typeName === "shape" &&
      String(record.parentId ?? "").startsWith("page:"),
  );
}

/**
 * Hold a plane to its rule, inside a change: each drawn shape has its thread,
 * and a thread with nothing said whose shape is gone goes with it. One with
 * a conversation stays, its mark outdated. Run on every write and on load,
 * so marks drawn before the rule get their threads too.
 */
export function keepMarkThreads(
  doc: AnnotatedDoc,
  planeId: string,
  sourceHeads: string[],
): void {
  const plane = doc[`plane:${planeId}`];
  invariant(plane, `Unknown plane: ${planeId}`);
  const drawn = drawnShapes(plane);
  for (const shape of drawn) {
    const id = markThreadId(shape.id);
    doc[`comment:${id}`] ??= {
      id,
      anchor: {
        kind: "marks",
        plane: planeId,
        shapes: [shape.id],
        sourceHeads,
      },
      messages: {},
      resolved: false,
    };
  }
  const present = new Set(drawn.map((shape) => shape.id));
  for (const annotation of annotationsOf(doc)) {
    const { anchor } = annotation;
    if (
      anchor.kind === "marks" &&
      anchor.plane === planeId &&
      Object.keys(annotation.messages).length === 0 &&
      !anchor.shapes.some((shape) => present.has(shape))
    ) {
      delete doc[`comment:${annotation.id}`];
    }
  }
}

/** The shape a mark thread is about, while it is still drawn. */
export function markOf(
  doc: AnnotatedDoc,
  annotation: Annotation,
): PlaneRecord | undefined {
  const { anchor } = annotation;
  if (anchor.kind !== "marks") return undefined;
  const plane = doc[`plane:${anchor.plane}`];
  return anchor.shapes
    .map((id) => plane?.records[id])
    .find((record) => record !== undefined);
}

/** What a mark says in its own lettering (a callout's note), if anything. */
export function markText(record: PlaneRecord | undefined): string {
  const props = record?.props as { text?: unknown } | undefined;
  return typeof props?.text === "string" ? props.text.trim() : "";
}

/**
 * A thread's messages in order. A callout's lettering is its first message,
 * read off the drawing so the words live in one place, signed by whoever
 * drew it.
 */
export function threadMessagesOf(
  doc: AnnotatedDoc,
  annotation: Annotation,
): Message[] {
  const posted = Object.values(annotation.messages).sort(
    (a, b) => a.createdAt - b.createdAt,
  );
  const mark = markOf(doc, annotation);
  const text = markText(mark);
  if (!mark || !text) return posted;
  const meta = (mark.meta ?? {}) as {
    author?: string;
    authorName?: string;
    createdAt?: number;
  };
  const note: Message = {
    id: `note-${mark.id}`,
    author: meta.author ?? "",
    authorName: meta.authorName ?? "Member",
    body: text,
    createdAt: meta.createdAt ?? 0,
  };
  return [note, ...posted];
}
